import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/AuthProvider';

export interface BillingReturnProps {
  productId: string;
  onEntitled: () => void;
  /** Give up after this long and let the user retry by hand. */
  timeoutMs?: number;
}

/**
 * The page Lemon Squeezy returns to after a paid checkout.
 *
 * The critical thing: **entitlement is NOT ready when the browser lands here.** Payment success and
 * our subscription record are different events — ours arrives asynchronously by webhook, and the
 * customer's browser usually beats it back here (they're independent, with no ordering guarantee).
 * So we poll with backoff and say so, rather than showing the product and letting it 403.
 *
 * TODO(item 4): replace this poll with an explicit LS verify on return — ask our API, which asks
 * Lemon Squeezy, and apply the order through the same idempotent path the webhook uses. Blocked on
 * LS credentials. (There is no longer a ~60s principal cache to wait out — that was removed; the
 * remaining wait is purely the webhook race.)
 */
export function BillingReturn({ productId, onEntitled, timeoutMs = 90_000 }: BillingReturnProps) {
  const { client, reload } = useSession();
  const [gaveUp, setGaveUp] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const cancelled = useRef(false);

  const poll = useCallback(async (): Promise<boolean> => {
    const me = await client.me();
    // Entitled AND seated — a subscription alone isn't enough to use the product (the gate wants a
    // seat). apply.ts auto-assigns the purchaser's seat, so both should land together.
    return me.seatedModules.includes(productId);
  }, [client, productId]);

  useEffect(() => {
    cancelled.current = false;
    const started = Date.now();
    let delay = 1000;

    const tick = async (): Promise<void> => {
      if (cancelled.current) return;
      try {
        if (await poll()) {
          await reload();
          onEntitled();
          return;
        }
      } catch {
        /* keep polling — a transient error here is expected while things settle */
      }
      const spent = Date.now() - started;
      setElapsed(spent);
      if (spent > timeoutMs) return setGaveUp(true);
      delay = Math.min(delay * 1.5, 8000); // backoff: don't hammer while a webhook is in flight
      setTimeout(() => void tick(), delay);
    };

    void tick();
    return () => {
      cancelled.current = true;
    };
  }, [poll, reload, onEntitled, timeoutMs]);

  if (gaveUp) {
    return (
      <div className="huaca-state" data-testid="billing-return-timeout">
        <h1>התשלום התקבל — עדיין מפעילים</h1>
        <p>התשלום עבר, אך הגישה עדיין לא הופיעה. בדרך כלל זה נפתר מעצמו תוך מספר דקות.</p>
        <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
          בדקו שוב
        </button>
      </div>
    );
  }

  return (
    <div className="huaca-state" data-testid="billing-return">
      <h1>מגדירים את המנוי שלכם…</h1>
      <p>בדרך כלל זה לוקח כמה שניות.{elapsed > 15_000 ? ' עדיין עובדים על זה — רק רגע.' : ''}</p>
    </div>
  );
}
